import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { adminGetDiscoverySettings, adminUpdateDiscoverySettings } from '../../services/api';

const DEFAULTS = {
  who_liked_me_blur_for_free: true,
  who_viewed_me_blur_for_free: true,
  online_window_minutes: 15,
  track_profile_views: true,
  unlock_credit_cost: 1,
  new_members_days: 7,
};

const DiscoverySettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    try {
      const res = await adminGetDiscoverySettings();
      setSettings(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        who_liked_me_blur_for_free: !!settings.who_liked_me_blur_for_free,
        who_viewed_me_blur_for_free: !!settings.who_viewed_me_blur_for_free,
        online_window_minutes: Math.max(1, Math.min(1440, parseInt(settings.online_window_minutes) || 15)),
        track_profile_views: !!settings.track_profile_views,
        unlock_credit_cost: Math.max(0, parseInt(settings.unlock_credit_cost) || 0),
        new_members_days: Math.max(1, Math.min(90, parseInt(settings.new_members_days) || 7)),
      };
      const res = await adminUpdateDiscoverySettings(payload);
      setSettings(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setSettings({ ...settings, ...DEFAULTS });

  if (!settings) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center text-gray-500">Loading…</div>
    );
  }

  return (
    <div className="space-y-4" data-testid="discovery-settings-tab">
      <div className="bg-blue-50 border border-blue-100 text-blue-900 rounded-xl p-3 text-sm flex gap-2">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>These controls affect the whole platform. Changes take effect immediately for all members.</div>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
        <div>
          <h3 className="font-semibold text-gray-900 mb-1">Blur & unlock rules</h3>
          <p className="text-xs text-gray-500 mb-4">Choose whether free members see other members’ photos blurred in Who-Liked-Me and Who-Viewed-Me. Premium members always see everything.</p>

          <div className="space-y-3">
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <div className="font-medium text-gray-900">Blur photos on “Who Liked Me” for free users</div>
                <div className="text-xs text-gray-500">Free members can see they have likes, but photos stay blurred until unlocked or upgraded.</div>
              </div>
              <Switch
                checked={!!settings.who_liked_me_blur_for_free}
                onCheckedChange={v => update('who_liked_me_blur_for_free', v)}
                data-testid="toggle-likes-blur"
              />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <div className="font-medium text-gray-900">Blur photos on “Who Viewed Me” for free users</div>
                <div className="text-xs text-gray-500">Same rules but for profile viewers.</div>
              </div>
              <Switch
                checked={!!settings.who_viewed_me_blur_for_free}
                onCheckedChange={v => update('who_viewed_me_blur_for_free', v)}
                data-testid="toggle-views-blur"
              />
            </label>

            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              <div>
                <Label className="text-xs text-gray-600">Credits to unlock a single photo</Label>
                <Input type="number" min="0" value={settings.unlock_credit_cost}
                  onChange={e => update('unlock_credit_cost', e.target.value)}
                  className="mt-1 rounded-lg" data-testid="unlock-cost" />
                <p className="text-[11px] text-gray-500 mt-1">Set to 0 to allow free unlocks.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-6">
          <h3 className="font-semibold text-gray-900 mb-1">Online status window</h3>
          <p className="text-xs text-gray-500 mb-3">A member is shown as “Online now” if they were active in the past <strong>{settings.online_window_minutes}</strong> minute{settings.online_window_minutes === 1 ? '' : 's'}.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-600">Window (minutes)</Label>
              <Input type="number" min="1" max="1440" value={settings.online_window_minutes}
                onChange={e => update('online_window_minutes', e.target.value)}
                className="mt-1 rounded-lg" data-testid="online-window" />
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-6">
          <h3 className="font-semibold text-gray-900 mb-1">New Members window</h3>
          <p className="text-xs text-gray-500 mb-3">Members who joined in the last <strong>{settings.new_members_days}</strong> day{settings.new_members_days === 1 ? '' : 's'} show up in the “New Members” section and filter.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-600">New members (days)</Label>
              <Input type="number" min="1" max="90" value={settings.new_members_days}
                onChange={e => update('new_members_days', e.target.value)}
                className="mt-1 rounded-lg" data-testid="new-members-days" />
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-6">
          <h3 className="font-semibold text-gray-900 mb-1">Profile view tracking</h3>
          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div>
              <div className="font-medium text-gray-900">Record profile views</div>
              <div className="text-xs text-gray-500">Powers the “Who Viewed Me” feature. Disable to stop collecting views platform-wide.</div>
            </div>
            <Switch
              checked={!!settings.track_profile_views}
              onCheckedChange={v => update('track_profile_views', v)}
              data-testid="toggle-track-views"
            />
          </label>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
          <Button onClick={handleSave} disabled={saving} className="bg-[#009E60] hover:bg-[#007A4B] rounded-full" data-testid="save-discovery-settings">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save changes'}
          </Button>
          <Button variant="ghost" onClick={handleReset} data-testid="reset-discovery-settings">
            <RotateCcw className="w-4 h-4 mr-2" /> Reset to defaults
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DiscoverySettingsTab;
